import { FC, memo, useState } from 'react';
import { useBroadcastEditorStore } from '../stores';
import { cn, stanAnalytics } from '@/lib';
import { useUser } from '@/queries';
import IcThumbsUp from '@/assets/icons/ic-thumbs-up.svg';
import IcThumbsDown from '@/assets/icons/ic-thumbs-down.svg';
import { useGenerateFeedbackMutation } from '../queries/useGenerateFeedbackMutation';

interface FeedbackButtonProps {
    type: 'up' | 'down';
    isActive: boolean;
    isDisabled: boolean;
    onClick: () => void;
}

const FeedbackButton: FC<FeedbackButtonProps> = ({ type, isActive, isDisabled, onClick }) => {
    const Icon = type === 'up' ? IcThumbsUp : IcThumbsDown;
    const label = type === 'up' ? 'Thumbs up' : 'Thumbs down';

    return (
        <button
            type='button'
            onClick={onClick}
            disabled={isDisabled}
            className={cn(
                'hover:bg-neutral-subtle grid size-6 cursor-pointer place-items-center rounded-sm transition-colors disabled:cursor-not-allowed',
                isActive && 'bg-brand-subtle',
                isDisabled && 'pointer-events-none'
            )}
            aria-label={label}
        >
            <Icon
                className={cn('stroke-brand-main size-4 transition-opacity', isDisabled && !isActive && 'opacity-50')}
            />
        </button>
    );
};

export const GeneratedDraftFeedback: FC = memo(() => {
    const { user } = useUser();
    const generatedDraft = useBroadcastEditorStore(state => state.generatedDraft);
    const setGeneratedDraft = useBroadcastEditorStore(state => state.setGeneratedDraft);
    const { mutate: submitFeedback, isPending } = useGenerateFeedbackMutation();
    const [showThanks, setShowThanks] = useState(false);

    if (!generatedDraft) return null;

    const handleFeedback = (feedbackValue: 1 | -1) => {
        if (!user || generatedDraft.feedback_status !== 0 || isPending) return;

        // Track analytics
        stanAnalytics('stanley-email-draft-feedback', {
            meta: { user_id: user.user_id, username: user.username },
            props: {
                response_id: generatedDraft.response_id,
                feedback_value: feedbackValue,
            },
        });

        // Submit feedback
        submitFeedback(
            {
                responseId: generatedDraft.response_id,
                feedbackValue,
            },
            {
                onSuccess: () => {
                    // Update store
                    setGeneratedDraft({
                        ...generatedDraft,
                        feedback_status: feedbackValue,
                    });

                    setShowThanks(true);
                },
            }
        );
    };

    const hasFeedback = generatedDraft.feedback_status !== 0;
    const isPositive = generatedDraft.feedback_status === 1;
    const isNegative = generatedDraft.feedback_status === -1;

    return (
        <div className='text-body-sm text-gray relative flex items-center justify-end gap-1.5 overflow-hidden'>
            {/* Initial state */}
            <div
                className={cn(
                    'will-change-opacity flex items-center gap-1.5 transition-opacity duration-300',
                    showThanks ? 'pointer-events-none opacity-0' : 'opacity-100'
                )}
            >
                <span>💬 Was this draft helpful?</span>
                <div className='flex items-center gap-1'>
                    <FeedbackButton
                        type='up'
                        isActive={isPositive}
                        isDisabled={hasFeedback || isPending}
                        onClick={() => handleFeedback(1)}
                    />
                    <FeedbackButton
                        type='down'
                        isActive={isNegative}
                        isDisabled={hasFeedback || isPending}
                        onClick={() => handleFeedback(-1)}
                    />
                </div>
            </div>

            {/* Thanks state */}
            <div
                className={cn(
                    'will-change-opacity absolute inset-0 flex items-center justify-end gap-1.5 transition-opacity duration-300',
                    showThanks ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
            >
                <span className='font-medium'>Thanks for the feedback!</span>
                <div className='flex items-center gap-1'>
                    <FeedbackButton type='up' isActive={isPositive} isDisabled onClick={() => {}} />
                    <FeedbackButton type='down' isActive={isNegative} isDisabled onClick={() => {}} />
                </div>
            </div>
        </div>
    );
});

GeneratedDraftFeedback.displayName = 'GeneratedDraftFeedback';
